import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/tailwind';

export function DatasetResizeHandle(props: {
  columnIdx: number;
  tableHeight: number;
  setColumnWidthStorage: (fromPrev: (prev: number[]) => number[]) => void;
  minColumnWidth?: number;
}) {
  const [dragging, setDragging] = useState(false);
  const handleRef = useRef<(() => void) | null>(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      handleRef.current?.();
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();

    const initialX = e.clientX;
    const cells = document.querySelectorAll(
      `[data-column="${props.columnIdx}"]`
    );
    const firstCell = cells[0];
    let initialWidth = 100;
    if (firstCell instanceof HTMLElement) {
      initialWidth = firstCell?.offsetWidth ?? 0;
    }

    function getConstrainedWidth(width: number) {
      return Math.max(width, props.minColumnWidth ?? 100);
    }

    function handlePointerMove(e: PointerEvent) {
      const diffX = e.clientX - initialX;

      const newWidth = getConstrainedWidth(initialWidth + diffX);

      cells.forEach((c) => {
        if (c instanceof HTMLElement) {
          c.style.setProperty('width', `${newWidth}px`);
          c.style.setProperty('min-width', `${newWidth}px`);
          c.style.setProperty('max-width', `${newWidth}px`);
        }
      });
    }

    function handlePointerUp(e: PointerEvent) {
      const diffX = e.clientX - initialX;
      const newWidth = getConstrainedWidth(initialWidth + diffX);
      props.setColumnWidthStorage((prev) => {
        const newWidths = [...prev];
        newWidths[props.columnIdx] = newWidth;
        return newWidths;
      });
      handleRef.current?.();
    }

    if (isMountedRef.current) setDragging(true);
    handleRef.current?.();

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    handleRef.current = () => {
      if (isMountedRef.current) setDragging(false);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  };

  const handlePointerUp = () => {
    handleRef.current?.();
    handleRef.current = null;
  };

  return (
    <div
      className={cn(
        'group absolute right-0 top-0 w-px cursor-ew-resize bg-quaternary'
      )}
      style={{ height: props.tableHeight }}
      onMouseDown={handlePointerDown}
      onMouseUp={handlePointerUp}
    >
      <div
        className={cn(
          'absolute inset-y-0 -left-[0.5px] z-10 w-[2px] bg-quaternary opacity-0 transition-all group-hover:opacity-100',
          dragging && 'bg-brand-green-400 opacity-100'
        )}
      />
      <div className="absolute inset-y-0 -left-[4px] z-10 w-[9px] opacity-0" />
    </div>
  );
}
